import { CompanyAdministrationPanelModel } from '../CompanyInfo/Administration/CompanyAdministrationPanelModel';
import { AdminPageTitle, AdminPanel } from '../CompanyInfo/Administration/Design';
import { LoadingOverlay, Group, Space, Box, Switch, TextInput, NumberInput, MultiSelect, Textarea } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { CompanyConfigService } from '@root/Services/Customers/CompanyConfigService';
import { useDi } from '@root/Services/DI';
import { SwitchContainer } from '../CompanyInfo/Administration/AdminAppAccess';
import { Company, CompanyConfiguration } from '@apis/Customers/model';

export interface CompanyConfigInfoProps {
    model: CompanyAdministrationPanelModel;
    company: Company;
}

export const CompanyConfigInfo = (props: CompanyConfigInfoProps) => {
    const companyConfigSvc = useDi(CompanyConfigService);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [curThrottling, setCurThrottling] = useState<string>('');
    const [mapWorkload, setMapWorkload] = useState<boolean>(false);
    const [blockExport, setBlockExport] = useState<boolean>(false);
    const [syncResources, setSyncResources] = useState<boolean>(false);
    const [isResale, setIsResale] = useState<boolean>(false);
    const [focusEnabled, setFocusEnabled] = useState<boolean>(false);
    const [awsRoleNameOverride, setAwsRoleNameOverride] = useState<string>('');
    const [awsExternalIdOverride, setAwsExternalIdOverride] = useState<string>('');
    const [archeraApiKey, setArcheraApiKey] = useState<string>('');
    const [archeraOrgId, setArcheraOrgId] = useState<string>('');
    const [azureStorageAccountOverride, setAzureStorageAccountOverride] = useState<string>('');
    const [curThrottlingError, setCurThrottlingError] = useState<string | null>(null);
    const [forecastMaxPredictionDays, setForecastMaxPredictionDays] = useState<number>(0);
    const [enabledRollupTypes, setEnabledRollupTypes] = useState<string[]>([]);

    const rollupTypes = ['BasicDailyRollupIndexer', 'DailySpendByResourceId', 'BasicMonthlyRollupIndexer', 'BillingReconciliationRollupIndexer'];

    useEffect(() => {
        (async () => {
            const companyConfig = await companyConfigSvc.getTypedCompanyConfig();
            setCurThrottling(companyConfig.curThrottling ?? '');
            setMapWorkload(companyConfig.requireMapWorkload);
            setBlockExport(companyConfig.blockExport);
            setSyncResources(props.company.SyncResources ?? false);
            setIsResale(props.company.IsResale ?? false);
            setFocusEnabled(companyConfig.focusEnabled);
            setAwsRoleNameOverride(companyConfig.awsRoleNameOverride ?? '');
            setAwsExternalIdOverride(companyConfig.awsExternalIdOverride ?? '');
            setForecastMaxPredictionDays(parseInt(companyConfig.forecastMaxPredictionDays ?? '0'));
            setEnabledRollupTypes(companyConfig.enabledRollupTypes ? JSON.parse(companyConfig.enabledRollupTypes) : []);
            setArcheraApiKey(companyConfig.archeraApiKey ?? '');
            setArcheraOrgId(companyConfig.archeraOrgId ?? '');
        })();
        setIsLoading(false);
    }, []);

    const handleCurThrottlingChange = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value;
        setCurThrottling(value);
        if (value === '' || isValidTime(value)) {
            setCurThrottlingError(null);
        } else {
            setCurThrottlingError('Format is not matching DD.hh:mm:ss or HH:mm:ss');
        }
    };
    const isValidTime = (value: string) => /^(\d+\.\d{2}:\d{2}:\d{2}|([01]?\d|2[0-3]):[0-5]\d:[0-5]\d)$/.test(value);
    const handleForecastMaxPredictionDays = (value: number) => {
        setForecastMaxPredictionDays(value);
    };

    const handleEnabledRollupTypes = (value: string[]) => {
        setEnabledRollupTypes(value);
        saveConfig('EnabledRollupTypes', value.length === 0 ? '' : JSON.stringify(value));
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>, key: string) => {
        if (key === 'CurThrottling' && curThrottlingError) {
            return;
        }

        let value: any = event.target.value;
        saveConfig(key, value);
    };

    const handleTextareaBlur = (event: React.FocusEvent<HTMLTextAreaElement>, key: string) => {
        let value: any = event.target.value;
        saveConfig(key, value);
    };

    const handleTextareaChange = (event: React.ChangeEvent<HTMLTextAreaElement>, key: string) => {
        if (key === 'AzureStorageAccountOverride') {
            setAzureStorageAccountOverride(event.currentTarget.value);
            return;
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>, key: string) => {
        const { checked } = event.currentTarget;
        let value: any = checked;
        if (key === 'MapWorkload' && mapWorkload !== checked) {
            setMapWorkload(checked);
        }
        if (key === 'Export') {
            setBlockExport(checked);
        }
        if (key === 'SyncResources') {
            setSyncResources(checked);
        }
        if (key === 'IsResale') {
            setIsResale(checked);
        }
        if (key === 'FocusEnabled') {
            setFocusEnabled(checked);
        }
        if (key === 'AwsRoleNameOverride') {
            setAwsRoleNameOverride(event.currentTarget.value);
            return;
        }
        if (key === 'AwsExternalIdOverride') {
            setAwsExternalIdOverride(event.currentTarget.value);
            return;
        }
        if (key === 'ArcheraApiKey') {
            setArcheraApiKey(event.currentTarget.value);
            return;
        }
        if (key === 'ArcheraOrgId') {
            setArcheraOrgId(event.currentTarget.value);
            return;
        }
        if (key === 'AzureStorageAccountOverride') {
            setAzureStorageAccountOverride(event.currentTarget.value);
            return;
        }
        saveConfig(key, value);
    };

    const saveConfig = async (key: string, value: any) => {
        setIsLoading(true);
        var saveValue = value ? 'true' : 'false';
        if (key === 'Export') {
            saveValue = value ? 'Block' : '';
        }

        if (key === 'MapWorkload') {
            saveValue = value ? 'Require' : '';
        }

        if (
            key === 'AwsRoleNameOverride' ||
            key === 'AwsExternalIdOverride' ||
            key === 'CurThrottling' ||
            key === 'EnabledRollupTypes' ||
            key === 'ArcheraApiKey' ||
            key === 'ArcheraOrgId' ||
            key === 'AzureStorageAccountOverride'
        ) {
            saveValue = value ? value : '';
        }

        if (key === 'ForecastMaxPredictionDays') {
            saveValue = value ? value.toString() : '';
        }
        try {
            await companyConfigSvc.saveCompanyConfigs([
                {
                    Key: key,
                    Value: saveValue,
                    CompanyId: props.company.Id,
                } as CompanyConfiguration,
            ]);
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AdminPanel>
            <LoadingOverlay visible={isLoading} />
            <Group position="apart">
                <AdminPageTitle style={{ display: 'inline-block' }}>Company Configs</AdminPageTitle>
            </Group>
            <Space h="sm" />
            <Box>
                <SwitchContainer>
                    <Switch
                        key={'mapWorkload'}
                        id={'mapWorkload'}
                        checked={mapWorkload}
                        onChange={(event) => handleChange(event, 'MapWorkload')}
                        label="Map Workload"
                    />
                </SwitchContainer>
                <Space h="sm" />
                <SwitchContainer>
                    <Switch
                        key={'blockExport'}
                        id={'blockExport'}
                        checked={blockExport}
                        onChange={(event) => handleChange(event, 'Export')}
                        label="Block Export"
                    />
                </SwitchContainer>
                <Space h="sm" />
                <SwitchContainer>
                    <Switch
                        key={'syncResources'}
                        id={'syncResources'}
                        checked={syncResources}
                        onChange={(event) => handleChange(event, 'SyncResources')}
                        label="Sync Resources"
                    />
                </SwitchContainer>
                <Space h="sm" />
                <SwitchContainer>
                    <Switch
                        key={'isResale'}
                        id={'isResale'}
                        checked={isResale}
                        onChange={(event) => handleChange(event, 'IsResale')}
                        label="Is Resale"
                    />
                </SwitchContainer>
                <Space h="sm" />
                <SwitchContainer>
                    <Switch
                        key={'focusEnabled'}
                        id={'focusEnabled'}
                        checked={focusEnabled}
                        onChange={(event) => handleChange(event, 'FocusEnabled')}
                        label="Focus Enabled"
                    />
                </SwitchContainer>
                <Space h="sm" />
                <div>
                    <TextInput
                        label="Cur Throttling"
                        value={curThrottling}
                        onChange={handleCurThrottlingChange}
                        onBlur={(event) => handleBlur(event, 'CurThrottling')}
                        placeholder="Enter Cur Throttling"
                        size="sm"
                        mt="sm"
                        error={curThrottlingError}
                        description="Enter time as DD:hh:mm:ss (e.g., 7.00:00:00) or HH:mm:ss (e.g., 18:00:00)"
                    />
                </div>
                <Space h="sm" />
                <div>
                    <TextInput
                        label="AWS Role Name Override"
                        value={awsRoleNameOverride}
                        onChange={(e) => handleChange(e, 'AwsRoleNameOverride')}
                        onBlur={(event) => handleBlur(event, 'AwsRoleNameOverride')}
                        placeholder="Enter AWS Role Name Override"
                        size="sm"
                        mt="sm"
                    />
                </div>
                <Space h="sm" />
                <div>
                    <TextInput
                        label="AWS External ID Override"
                        value={awsExternalIdOverride}
                        onChange={(e) => handleChange(e, 'AwsExternalIdOverride')}
                        onBlur={(event) => handleBlur(event, 'AwsExternalIdOverride')}
                        placeholder="Enter AWS External ID Override"
                        size="sm"
                        mt="sm"
                    />
                </div>
                <Space h="sm" />
                <div>
                    <NumberInput
                        label="Forecast Max Prediction Days"
                        value={forecastMaxPredictionDays}
                        onChange={handleForecastMaxPredictionDays}
                        onBlur={(event) => handleBlur(event, 'ForecastMaxPredictionDays')}
                        placeholder="Enter Forecast Max Prediction Days"
                        size="sm"
                        mt="sm"
                    />
                </div>
                <Space h="sm" />
                <div>
                    <MultiSelect
                        label="Enabled Rollup Types"
                        data={rollupTypes}
                        value={enabledRollupTypes}
                        onChange={handleEnabledRollupTypes}
                        placeholder="Select Enabled Rollup Types"
                        size="sm"
                        mt="sm"
                    />
                </div>
                <Space h="sm" />
                <div>
                    <TextInput
                        label="Archera Api Key"
                        value={archeraApiKey}
                        onChange={(e) => handleChange(e, 'ArcheraApiKey')}
                        onBlur={(event) => handleBlur(event, 'ArcheraApiKey')}
                        placeholder="Enter Archera Api Key"
                        size="sm"
                        mt="sm"
                    />
                </div>
                <Space h="sm" />
                <div>
                    <TextInput
                        label="Archera Organization Id"
                        value={archeraOrgId}
                        onChange={(e) => handleChange(e, 'ArcheraOrgId')}
                        onBlur={(event) => handleBlur(event, 'ArcheraOrgId')}
                        placeholder="Enter Archera Organization Id"
                        size="sm"
                        mt="sm"
                    />
                </div>
                <Space h="sm" />
                <div>
                    <Textarea
                        label="Azure Storage Account Configuration"
                        value={azureStorageAccountOverride}
                        onChange={(e) => handleTextareaChange(e, 'AzureStorageAccountOverride')}
                        onBlur={(event) => handleTextareaBlur(event, 'AzureStorageAccountOverride')}
                        placeholder={`Enter JSON configuration:\n[{\n  "SubscriptionId": "your-subscription-id",\n  "StorageAccountName": "your-storage-account-name"\n}]`}
                        size="sm"
                        mt="sm"
                        minRows={4}
                        maxRows={10}
                        autosize
                    />
                </div>
            </Box>
        </AdminPanel>
    );
};
